import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  analysisManagerFiltersAtom,
  analysisManagerQueryAtom,
  analysisManagerSanitizerQueryAtom,
} from 'r/atoms';
import type { Analyse, ScoreMarket } from 'r/types';
import PaginationControls from './PaginationControls';
import ManualAnalysisInputPanel from './ManualAnalysisInputPanel';
import useRestoreAnalysis from './useRestoreAnalysis';
import useAnalysisManagerActions from './useAnalysisManagerActions';
import AnalysisCard from './AnalysisCard';
import AnalysisManagerFilters from './AnalysisManagerFilters';
import { useScoreMarketsByAnalysis } from '../ScoreMarketsTab/queries';
import { SpinnerCircle } from 'r/ui';

export default function AnalysisManagerTab() {
  const [filters, setFilters] = useAtom(analysisManagerFiltersAtom);
  const mainQuery = useAtomValue(analysisManagerQueryAtom);
  const sanitizerQuery = useAtomValue(analysisManagerSanitizerQueryAtom);

  const analyses = mainQuery.data?.data || [];
  const total = mainQuery.data?.total || 0;
  const sanitizerAnalyses = sanitizerQuery.data?.data || [];
  const isPending = mainQuery.isLoading;
  const isFetching = mainQuery.isFetching;
  const refetch = () => mainQuery.refetch();

  // Local UI state
  const [expandedTextId, setExpandedTextId] = useState<string | null>(null);
  const [selectedAnalyses, setSelectedAnalyses] = useState<Analyse[]>([]);

  // Hooks
  const {
    handleVerify: verifyAnalysis,
    handleAddToSanitizer,
    isPending: isActionPending,
  } = useAnalysisManagerActions();

  const handleVerify = useCallback(
    (analysisId: string, onSuccess?: () => void) => {
      verifyAnalysis(analysisId, {
        onSuccess: () => {
          setSelectedAnalyses([]);
          onSuccess?.();
        },
      });
    },
    [verifyAnalysis],
  );
  const { mutateAsync: restore, isPending: isRestoring } = useRestoreAnalysis({
    onSuccess: refetch,
  });

  const isSubmitting = isActionPending || isRestoring;

  // Memoize ids to prevent unnecessary refetches
  const analysisIds = useMemo(
    () => (analyses.length > 0 ? analyses.map((item) => item.id) : undefined),
    [analyses],
  );
  const { data: markets } = useScoreMarketsByAnalysis(analysisIds);

  // Derived
  const totalPages = Math.ceil(total / filters.pageSize);
  const hasActiveFilters = !!(
    filters.searchQuery ||
    filters.filterType !== 'all' ||
    filters.authorFilterMode !== 'all' ||
    filters.excludedAuthors.length > 0 ||
    (filters.selectedPair && filters.selectedPair.length > 0)
  );

  // Helpers
  const isInSanitizerList = useCallback(
    (analysis: Analyse) =>
      sanitizerAnalyses.some((item) => item.tweetID === analysis.tweetID),
    [sanitizerAnalyses],
  );

  const getExistingMarket = useCallback(
    (analysis: Analyse): ScoreMarket | undefined =>
      markets?.find((market) => market.analysisId === analysis.id),
    [markets],
  );

  const getSanitizerAnalysis = useCallback(
    (analysis: Analyse): Analyse | undefined =>
      sanitizerAnalyses.find((item) => item.tweetID === analysis.tweetID),
    [sanitizerAnalyses],
  );

  // Handlers
  const handleToggleSelect = useCallback((analysis: Analyse) => {
    setSelectedAnalyses((prev) => {
      const isSelected = prev.some((a) => a.id === analysis.id);
      return isSelected ?
          prev.filter((a) => a.id !== analysis.id)
        : [...prev, analysis];
    });
  }, []);

  useEffect(() => {
    setSelectedAnalyses([]);
  }, [filters.showDeletedOnly]);

  const handleBulkSuccess = useCallback(() => {
    setSelectedAnalyses([]);
    refetch();
  }, [refetch]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setFilters((prev) => ({ ...prev, currentPage: page }));
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newCurrentPage = Math.ceil(
      ((filters.currentPage - 1) * filters.pageSize + 1) / newPageSize,
    );
    setFilters((prev) => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: newCurrentPage,
    }));
  };

  return (
    <div className="w-full bg-bg-main py-6 font-sans">
      <div className="max-w-7xl mx-auto flex flex-col gap-8">
        {/* Block A: Manual Analysis Input Panel */}
        <ManualAnalysisInputPanel refetchList={refetch} />

        {/* Block B: Analyses Display & Management Panel */}
        <div className="bg-background rounded-2xl p-6 shadow-sm border border-bg-accent-tabs">
          {/* Filter and Search Bar */}
          <AnalysisManagerFilters isFetching={isFetching} />

          {/* Results Summary */}
          <div className="text-sm text-secondary mb-6 font-medium">
            {hasActiveFilters ?
              `Found ${total} result${total !== 1 ? 's' : ''}`
            : `Total: ${total} analyses`}
            {isFetching && (
              <span className="ml-2 text-blue-500 animate-pulse">
                Updating...
              </span>
            )}
          </div>

          {/* Selection Bar */}
          {selectedAnalyses.length > 0 && !filters.showDeletedOnly && (
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="flex items-center gap-2 text-sm text-secondary">
                {selectedAnalyses.length} selected
                <button
                  onClick={() => setSelectedAnalyses([])}
                  className="text-focus hover:underline cursor-pointer"
                >
                  Unselect all
                </button>
              </span>
            </div>
          )}

          {/* Analysis Cards Grid */}
          {isPending && analyses.length === 0 ?
            <div className="flex justify-center py-16">
              <SpinnerCircle size={32} color="var(--muted-foreground)" />
            </div>
          : <>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {analyses.map((analysis) => (
                  <AnalysisCard
                    key={analysis.id}
                    analysis={analysis}
                    isSelected={selectedAnalyses.some(
                      (a) => a.id === analysis.id,
                    )}
                    selectedAnalyses={selectedAnalyses}
                    showDeletedOnly={filters.showDeletedOnly}
                    isSubmitting={isSubmitting}
                    isInSanitizer={isInSanitizerList(analysis)}
                    market={getExistingMarket(analysis)}
                    expandedTextId={expandedTextId}
                    getSanitizerAnalysis={getSanitizerAnalysis}
                    getExistingMarket={getExistingMarket}
                    onToggleSelect={handleToggleSelect}
                    onToggleExpand={setExpandedTextId}
                    onVerify={handleVerify}
                    onAddToSanitizer={handleAddToSanitizer}
                    onRestore={restore}
                    onBulkSuccess={handleBulkSuccess}
                  />
                ))}
              </div>

              {/* Empty state */}
              {analyses.length === 0 && !isPending && (
                <div className="col-span-full min-h-85 flex items-center justify-center text-center text-secondary py-16 border-2 border-dashed border-bg-accent-tabs rounded-xl">
                  {hasActiveFilters ?
                    <div>
                      <p className="text-lg font-medium text-main mb-1">
                        No results found
                      </p>
                      <p className="text-sm">
                        Try adjusting your search or filters.
                      </p>
                    </div>
                  : <div>
                      <p className="text-lg font-medium text-main mb-1">
                        No analyses yet
                      </p>
                      <p className="text-sm">
                        Add your first analysis manually or wait for API
                        updates.
                      </p>
                    </div>
                  }
                </div>
              )}

              <PaginationControls
                page={filters.currentPage - 1}
                total={total}
                limit={filters.pageSize}
                onPageChange={(p) => handlePageChange(p + 1)}
                onLimitChange={handlePageSizeChange}
              />
            </>
          }
        </div>
      </div>
    </div>
  );
}
